import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const StripeReturn: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUserFromToken, loading } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your subscription...');
  const [hasProcessed, setHasProcessed] = useState(false);

  const subscriptionSuccess = searchParams.get('subscription_success') === 'true';

  useEffect(() => {
    const handleStripeReturn = async () => {
      try {
        console.log('[STRIPE RETURN] Starting handleStripeReturn');
        console.log('[STRIPE RETURN] Loading:', loading);
        console.log('[STRIPE RETURN] User:', user);
        console.log('[STRIPE RETURN] Subscription success:', subscriptionSuccess);

        // Wait for auth to finish loading
        if (loading) {
          setMessage('Authenticating...');
          console.log('[STRIPE RETURN] Still loading, waiting...');
          return;
        }

        // Prevent re-running after first attempt
        if (hasProcessed) {
          console.log('[STRIPE RETURN] Already processed, skipping');
          return;
        }
        setHasProcessed(true);

        // Handle payment cancellation/failure - don't require auth
        if (!subscriptionSuccess) {
          console.log('[STRIPE RETURN] Payment was cancelled or failed');
          setStatus('error');
          setMessage('Payment was cancelled or declined. No charges were made. You can try again from your profile page.');
          setTimeout(() => navigate('/profile'), 3000);
          return;
        }

        // For successful payments, verify authentication
        if (!user) {
          console.error('[STRIPE RETURN] No user found after auth loaded');
          setStatus('error');
          setMessage('Authentication required. Please sign in to view your updated plan.');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        // Handle successful subscription
        setMessage('Subscription successful! Updating your account...');

        // Poll for plan update with retry logic (webhook may take a few seconds)
        const maxRetries = 10;
        const retryDelay = 1000; // 1 second
        let retryCount = 0;
        let planUpdated = false;

        while (retryCount < maxRetries && !planUpdated) {
          try {
            // Refresh the user data from the token
            const updatedUser = await refreshUserFromToken();

            if (!updatedUser) {
              console.error('[STRIPE RETURN] Failed to refresh user data');
              break;
            }

            // Check if plan has been updated
            const currentPlan = updatedUser?.plan || 'free';
            if (currentPlan === 'pro' || currentPlan === 'premium') {
              planUpdated = true;
              setStatus('success');
              setMessage('Account upgraded successfully! Your plan has been updated.');

              // Navigate to profile page
              setTimeout(() => {
                navigate('/profile');
              }, 2000);
              break;
            }

            // Wait before next retry
            if (retryCount < maxRetries - 1) {
              setMessage(`Subscription successful! Updating your account... (${retryCount + 1}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
            retryCount++;
          } catch (error) {
            console.error('[STRIPE RETURN] Error during plan update polling:', error);
            // Don't break the loop, try again
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
          }
        }

        // If plan still not updated after all retries
        if (!planUpdated) {
          setStatus('success');
          setMessage('Payment received! Your account will be upgraded shortly. Please refresh the page in a few moments.');
          setTimeout(() => navigate('/profile'), 4000);
        }
      } catch (error) {
        console.error('[STRIPE RETURN] Error handling Stripe return:', error);
        setStatus('error');
        setMessage('There was an issue processing your payment status. Please check your profile or contact support.');
        setTimeout(() => navigate('/profile'), 4000);
      }
    };

    handleStripeReturn();
  }, [subscriptionSuccess, navigate, refreshUserFromToken, loading, hasProcessed, user]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Processing</h2>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-5xl mb-4">✅</div>
              <h2 className="text-xl font-semibold text-green-600 mb-2">Success!</h2>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-5xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold text-amber-600 mb-2">Action Required</h2>
            </>
          )}

          <p className="text-gray-600">{message}</p>
        </div>
      </div>
    </div>
  );
};

export default StripeReturn;